/**
 * Progressive "load more" over ordinary Shopify pagination.
 *
 * Markup: a <load-more> wrapping a grid and a link to the next page. First
 * used by sections/search.liquid; built to be reused by the collection grid,
 * which is the same layout — hence the target and section id are attributes
 * rather than assumptions.
 *
 *   <load-more data-load-more-target="#SearchResultsGrid"
 *              data-load-more-section="{{ section.id }}"
 *              data-load-more-announcement="{{ 'search.more_loaded' | t }}">
 *     <ul id="SearchResultsGrid">…</ul>
 *     <div data-load-more-button><a href="{{ paginate.next.url }}">Load more</a></div>
 *     <p role="status" data-load-more-status></p>
 *   </load-more>
 *
 * The link is found through `[data-load-more-button]` rather than as the first
 * anchor in the element — the grid above it is full of product links.
 *
 * THE LINK IS REAL AND STAYS REAL. Without JavaScript it is ordinary
 * pagination that navigates to page 2 and works. This component intercepts the
 * click, fetches that same URL through the Section Rendering API, appends the
 * new cards and advances the link's href to page 3 — so the no-JS path and the
 * enhanced path walk the identical set of URLs.
 *
 * WHY A REAL ASSET AND NOT {% javascript %}: Shopify compiles every section's
 * JS into one bundle gated on `if (__sections__.<type>)` inside a single
 * shared try/catch, and in this theme that layer silently failed to define
 * <mega-menu> — the element was in the DOM, correctly styled, never upgraded,
 * with no error. Loaded by the section itself rather than the layout, since it
 * is needed on two templates rather than every page.
 *
 * THE URL IS DELIBERATELY NOT UPDATED as pages are appended. Rewriting it to
 * `?page=3` would mean a refresh showed page 3 alone, with the first 56
 * products missing and no way back to them.
 */
if (!customElements.get('load-more')) {
  customElements.define(
    'load-more',
    class LoadMore extends HTMLElement {
      connectedCallback() {
        this.init = this.init.bind(this);

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.init, { once: true });
        } else {
          this.init();
        }
      }

      init() {
        const grid = this.querySelector(this.dataset.loadMoreTarget);
        const button = this.querySelector('[data-load-more-button] a[href]');

        // No next page means the section rendered no link at all, which is the
        // normal end state rather than a fault.
        //
        // Assigned only AFTER the checks pass, so `this.button` is an honest
        // signal in disconnectedCallback that setup actually completed.
        if (!grid || !button) return;

        this.grid = grid;
        this.button = button;
        this.footer = this.querySelector('[data-load-more-button]');
        this.status = this.querySelector('[data-load-more-status]');

        this.handleClick = this.handleClick.bind(this);
        this.button.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.init);

        // Guard on a property set during setup, not on a method: methods live
        // on the prototype and are always truthy, so checking one would not
        // tell us whether init() actually ran.
        if (!this.button) return;

        this.button.removeEventListener('click', this.handleClick);
        this.button = null;
      }

      async handleClick(event) {
        event.preventDefault();

        if (this.loading) return;

        // Held locally because append() nulls `this.button` on the last page,
        // and the aria-busy reset below would then throw on it.
        const button = this.button;
        const next = button.getAttribute('href');
        if (!next) return;

        this.loading = true;
        button.setAttribute('aria-busy', 'true');

        try {
          const markup = await this.fetchPage(next);
          this.append(markup);
        } catch (error) {
          // Nothing is logged and nothing is destroyed: the grid keeps every
          // product it already has and the link keeps its href, so the shopper
          // can simply press it again. Falling back to a real navigation would
          // lose the pages already appended.
          this.announce('');
        }

        this.loading = false;
        button.removeAttribute('aria-busy');
      }

      /**
       * The Section Rendering API returns only this section, wrapped in
       * <div id="shopify-section-{id}">. Appending `section_id` to the
       * pagination URL keeps every other parameter — `q`, and any filters the
       * collection grid adds later — without having to know what they are.
       */
      async fetchPage(url) {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('section_id', this.dataset.loadMoreSection);

        const response = await fetch(target.toString());
        if (!response.ok) throw new Error(response.status);

        return response.text();
      }

      append(markup) {
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const incomingGrid = parsed.querySelector(this.dataset.loadMoreTarget);
        const incomingButton = parsed.querySelector('[data-load-more-button] a[href]');

        if (!incomingGrid) throw new Error('grid missing');

        const added = Array.from(incomingGrid.children);
        const first = added[0];

        this.grid.append(...added);

        // Advance to the page after the one just appended, or retire the
        // control entirely when the fetched page carried no link of its own.
        if (incomingButton) {
          this.button.setAttribute('href', incomingButton.getAttribute('href'));
        } else {
          this.button.removeEventListener('click', this.handleClick);
          this.button = null;

          // Remove the whole footer, not just the link — its 100px of vertical
          // padding would otherwise leave a gap under the last row.
          if (this.footer) this.footer.remove();
        }

        this.announce(this.dataset.loadMoreAnnouncement);

        // Move focus to the first card that just arrived. Without this a
        // keyboard user is left on a button that has either moved or gone, and
        // has to tab back through everything already on the page to reach the
        // new products.
        if (!first) return;

        const link = first.querySelector('a[href]');
        if (!link) return;

        link.setAttribute('tabindex', '-1');
        link.focus();
      }

      /** The status region is optional markup, so never assume it is there. */
      announce(message) {
        if (!this.status) return;

        this.status.textContent = message || '';
      }
    }
  );
}
